<template>
  <div class="filter-reset-panel">
    <div v-if="selection" class="selection-hint">
      {{
        t('generic.selectedItems', { count: selectionCount ?? selected.length })
      }}
    </div>
    <vf-button
      v-if="showResetFilterButton && !props.hideResetFilterButton"
      id="table-reset-filter-button"
      :size="ButtonSize.SMALL"
      :variant="ButtonVariant.TERTIARY"
      @click="resetFilters"
      >{{ t('filterableTable.resetAllFilters') }}</vf-button
    >
  </div>
  <PVDataTable
    v-if="items && fields && pageOptions && selected"
    id="ddp-project-table"
    ref="pv-data-table"
    v-model:expanded-rows="expandedRows"
    v-model:filters="filterObject"
    v-model:selection="selected"
    :filter-display="'row'"
    :first="skip"
    highlight-on-select
    :lazy="mode === TableMode.LAZY"
    :paginator="paginator"
    :pt="{
      bodyRow: {
        style: {
          cursor: rowClickable ? 'pointer' : 'default',
        },
      },
    }"
    :style="
      paginatorPosition === 'both'
        ? {
            borderTop: '1px solid',
            borderColor: 'var(--p-datatable-paginator-bottom-border-color)',
          }
        : {}
    "
    :rows="pageOptions.perPage"
    :rows-per-page-options="rowsPerPageOptions"
    :scrollable="false"
    :paginator-template="paginatorTemplate"
    :current-page-report-template="currentPageReportTemplate"
    :paginator-position="paginatorPosition"
    :sort-field="pageOptions.sortBy"
    :sort-order="sortOrder"
    :total-records="totalRecords"
    v-bind="$attrs"
    :value="items"
    @page="handlePageChange"
    @row-click="(e) => $emit('row-click', e.data)"
    @sort="handleSortChange"
  >
    <template #empty>
      <span v-if="props.emptyTableText" class="flex justify-center italic">{{
        t(props.emptyTableText)
      }}</span>
    </template>
    <PVColumn
      v-if="selection"
      selection-mode="multiple"
      :style="{ width: SELECTION_COLUMN_WIDTH }"
    ></PVColumn>
    <PVColumn
      v-if="expandableRows"
      expander
      :style="{ width: EXPANDER_COLUMN_WIDTH }"
    />
    <PVColumn
      v-for="field in fields"
      :key="field.key"
      :class="field.tdClass"
      :field="field.key.toString()"
      :filter-field="
        filterConfiguration?.[field.key]?.filterProperty?.toString() ??
        field.key.toString()
      "
      :header="'label' in field ? t(field.label) : undefined"
      :show-filter-menu="false"
      :sortable="field.sortable"
      :pt="{
        bodyCell: (props) => ({
          class:
            field.cellClassGetter?.(
              props.instance.rowData[field.key],
              field.key,
              props.instance.rowData,
            ) ?? [],
        }),
      }"
      :style="{ width: field.width }"
    >
      <template v-if="'icon' in field" #header>
        <FontAwesomeIcon :icon="field.icon" />
      </template>
      <template #body="{ data, index }">
        <div v-if="data">
          <slot
            v-if="$slots[getCellSlot(field)]"
            :name="getCellSlot(field)"
            :data="data"
          />
          <div v-else-if="field.key === 'thumbnail'">
            <div v-if="data.thumbnail" class="flex items-center min-h-14">
              <img
                v-if="data.thumbnail.large"
                class="max-h-14 max-w-14 h-auto w-auto border-1 border-neutral-400 flex items-center justify-center"
                :src="data.thumbnail.large.url"
                @click.stop="
                  () => openThumbnailPreview(data.thumbnail.large.url)
                "
              />
              <div v-else>
                <font-awesome-icon :icon="faFile" class="min-h-13 min-w-10" />
              </div>
            </div>
          </div>
          <div v-else-if="field.key === 'kks'">
            <div v-if="data.kks" class="flex items-center gap-1">
              <span v-for="kks in data.kks.slice(0, 2)" :key="kks">
                <PvTag :value="kks" severity="secondary" />
              </span>
              <span v-if="data.kks.length > 2" :title="data.kks.join(', ')">
                <PvTag
                  :value="`+${data.kks.length - 2}`"
                  severity="secondary"
                />
              </span>
            </div>
          </div>
          <div v-else-if="field.key === 'documentClass'">
            <div v-if="data.documentClass" class="flex items-center">
              <span
                ><PvTag :value="data.documentClass" severity="secondary"
              /></span>
            </div>
          </div>
          <div
            v-else
            :class="{ clickable: rowClickable || field.truncation }"
            @click="() => toggleCellExpansion(field.key.toString(), index)"
          >
            {{ getValue(field, index, data) }}
          </div>
        </div>
      </template>
      <template #filter="{ filterModel, filterCallback }">
        <PVInputText
          v-if="getFilterType(field) === FilterType.INPUT && filterModel"
          v-model="filterModel.value"
          :placeholder="getInputPlaceholder(field.key)"
          :style="{
            width: field.fluid
              ? '100%'
              : filterConfiguration?.[field.key]?.width,
          }"
          type="text"
          @input="delay(filterCallback)"
        />
        <PVInputNumber
          v-else-if="getFilterType(field) === FilterType.NUMBER && filterModel"
          v-model="filterModel.value"
          :placeholder="getInputPlaceholder(field.key)"
          :input-style="{
            width: field.fluid
              ? '100%'
              : filterConfiguration?.[field.key]?.width,
          }"
          @input="
            (e) => {
              filterModel.value = e.value;
              filterCallback();
            }
          "
        />
        <PVSelect
          v-else-if="getFilterType(field) === FilterType.SELECT && filterModel"
          v-model="filterModel.value"
          :default-value="SELECT_ALL"
          :options="selectValues[field.key]"
          :option-label="(value) => getSelectOptionLabel(value, field.key)"
          :style="{
            width: field.fluid
              ? '100%'
              : filterConfiguration?.[field.key]?.width,
          }"
          @change="filterCallback()"
        ></PVSelect>
        <PVDatePicker
          v-else-if="getFilterType(field) === FilterType.DATE_RANGE_PICKER"
          :model-value="getDateRange(field)"
          :placeholder="getInputPlaceholder(field.key)"
          selection-mode="range"
          @update:model-value="setDateRange(field, $event)"
        />
        <div
          v-else-if="
            getFilterType(field) === FilterType.CHECK_BOX && filterModel
          "
          class="checkbox-filter"
        >
          <PVCheckbox
            v-model="filterModel.value"
            binary
            :name="field.key.toString()"
            :input-id="field.key.toString()"
            @change="filterCallback()"
          />
          <label :for="field.key.toString()">
            {{ getInputPlaceholder(field.key) }}
          </label>
        </div>
      </template>
    </PVColumn>
    <PVColumn
      v-if="actionIcon && displayActionIcon"
      :style="{ width: ACTION_ICON_COLUMN_WIDTH }"
    >
      <template #body="{ data }">
        <div v-tooltip.top="actionIconTooltipText">
          <font-awesome-icon
            :class="{
              clickable: !actionIconDisabled,
              'disabled-icon': actionIconDisabled,
            }"
            :icon="getActionIcon()"
            @click.stop="() => $emit('action-icon-click', data)"
          />
        </div>
      </template>
    </PVColumn>
    <template #expansion="{ data }">
      <slot :data="data" name="expansion" />
    </template>
  </PVDataTable>
  <Dialog
    v-model:visible="showPreviewDialog"
    :header="t('workspaceDocuments.preview')"
    :style="{ width: '70%' }"
    :modal="true"
    :draggable="false"
    :resizable="false"
    @hide="selectedThumbnail = undefined"
  >
    <div class="flex w-full items-center justify-center">
      <img :src="selectedThumbnail" class="border-1 border-neutral-500" />
    </div>
  </Dialog>
</template>

<script lang="ts" setup generic="T extends object, U extends object = T">
  import PVInputText from 'primevue/inputtext';
  import {
    FilterConfiguration,
    Filtering,
    TableField,
    PageOptions,
    TableWrapperProps,
    SelectOptions,
    SortedFilters,
    TableMode,
    TablePaginationVariant,
    FilterConfigurations,
    FilterType,
  } from './TableWrapper.types';
  import { useI18n } from 'vue-i18n';
  import {
    DataTableFilterMeta,
    DataTablePageEvent,
    DataTableSortEvent,
  } from 'primevue/datatable';
  import { computed } from 'vue';
  import { Order } from 'types';
  import { debounce, isDate, mapValues, pickBy, truncate } from 'lodash';
  import PVSelect from 'primevue/select';
  import PVColumn from 'primevue/column';
  import PVDatePicker, { DatePickerProps } from 'primevue/datepicker';
  import PVCheckbox from 'primevue/checkbox';
  import PVDataTable from 'primevue/datatable';
  import Dialog from 'primevue/dialog';
  import {
    ACTION_ICON_COLUMN_WIDTH,
    EXPANDER_COLUMN_WIDTH,
    SELECT_ALL,
    SELECTION_COLUMN_WIDTH,
  } from './constants';
  import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
  import { ref } from 'vue';
  import { ButtonSize, ButtonVariant } from '../VfButton/VfButton.types';
  import PVInputNumber from 'primevue/inputnumber';
  import { useTemplateRef } from 'vue';
  import { watchEffect } from 'vue';
  import { watch } from 'vue';
  import {
    faBan,
    faTrash,
    IconDefinition,
  } from '@fortawesome/free-solid-svg-icons';
  import { faFile } from '@fortawesome/pro-regular-svg-icons';
  import PvTag from 'primevue/tag';

  const { t } = useI18n();

  const {
    mode = TableMode.CLIENT_SIDE,
    displayActionIcon = true,
    paginatorVariant = TablePaginationVariant.DEFAULT,
    hasNextPage = false,
    resetSelectionOnPageOrFilterChange = true,
    paginator = true,
    paginatorPosition = 'both',
    ...props
  } = defineProps<TableWrapperProps<T, U>>();
  const filters = defineModel<Filtering<U>>('filters', {
    default: () => ({}),
  });
  const pageOptions = defineModel<PageOptions<T>>('pageOptions', {
    default: () => ({
      page: 1,
      perPage: 9999,
      sortBy: '',
      sortOrder: Order.ASC,
      rows: 0,
    }),
  });
  const selected = defineModel<T[]>('selected', {
    default: () => [],
  });
  watch(
    [filters, pageOptions],
    () => {
      if (resetSelectionOnPageOrFilterChange) {
        selected.value = [];
      }
    },
    { deep: true },
  );
  const emit = defineEmits<{
    (e: 'action-icon-click', data: T): void;
    (e: 'filtering-reset'): void;
    (e: 'row-click', data: T): void;
  }>();

  const perPageOptions = [10, 20, 50, 100, 500, 99999];
  const showPreviewDialog = ref(false);
  const selectedThumbnail = ref<string | undefined>(undefined);
  const isCursorPagination = computed(
    () => paginatorVariant === TablePaginationVariant.CURSOR,
  );
  const paginatorTemplate = computed(() => {
    if (!isCursorPagination.value) {
      return undefined;
    }
    return 'PrevPageLink CurrentPageReport NextPageLink';
  });
  const currentPageReportTemplate = computed(() => {
    if (!isCursorPagination.value) {
      return undefined;
    }
    return '{currentPage}';
  });
  const rowsPerPageOptions = computed(() => {
    if (isCursorPagination.value) {
      return undefined;
    }
    return perPageOptions;
  });

  const sortOrder = computed<number>(() => {
    return pageOptions.value.sortOrder === Order.ASC ? 1 : -1;
  });

  const expandedRows = ref([]);

  const filterObject = computed<DataTableFilterMeta>({
    get() {
      if (!props.filterConfiguration) return {};
      return Object.entries(props.filterConfiguration).reduce(
        (filterObject, entry) => {
          const config = entry[1] as FilterConfiguration<U>;
          const field = (config.filterProperty ?? entry[0]) as keyof U & string;
          const filterValue = filters.value[config.filterProperty ?? field];
          if (filterValue === SELECT_ALL) {
            filterObject[field] = {
              value: filterValue,
              /**
               * Workaround to disable this filter in `TableMode.CLIENT_SIDE`
               */
              matchMode: 'notEquals',
            };
          } else if (config.type === FilterType.INPUT) {
            filterObject[field] = { value: filterValue, matchMode: 'contains' };
          } else if (config.type === FilterType.NUMBER) {
            filterObject[field] = {
              value: filterValue,
              matchMode: 'contains',
            };
          } else if (
            [FilterType.SELECT, FilterType.CHECK_BOX].includes(config.type)
          ) {
            filterObject[field] = { value: filterValue, matchMode: 'equals' };
          } else if (config?.type === FilterType.DATE_RANGE_PICKER) {
            const dateFrom = filters.value[config.dateFromKey];
            const dateTo = filters.value[config.dateToKey];
            filterObject[config.dateFromKey as string] = {
              value: dateFrom,
              matchMode: 'dateAfter',
            };
            filterObject[config.dateToKey as string] = {
              value: dateTo,
              matchMode: 'dateBefore',
            };
          }
          return filterObject;
        },
        {} as DataTableFilterMeta,
      );
    },
    set(pvFilters) {
      filters.value = mapValues(
        pvFilters,
        (value) => (value as unknown as DataTableFilterMeta).value,
      ) as Filtering<U>;
    },
  });

  const skip = computed(() => {
    return pageOptions.value.perPage * (pageOptions.value.page - 1);
  });
  const totalRecords = computed(() => {
    if (!isCursorPagination.value) {
      return pageOptions.value.rows;
    }

    // PrimeVue paginator requires a total count, so cursor mode synthesizes the
    // smallest value that keeps Previous/Next navigation accurate.
    return Math.max(1, skip.value + props.items.length + (hasNextPage ? 1 : 0));
  });

  const showResetFilterButton = ref(false);
  const table = useTemplateRef('pv-data-table');
  watchEffect(() => {
    if (table.value) {
      showResetFilterButton.value = !!table.value?.$props.filterDisplay;
    }
  });
  function resetFilters() {
    filters.value = {};
    emit('filtering-reset');
  }

  function getDateRange(
    field: TableField<T>,
  ): DatePickerProps['modelValue'] | null {
    const config = props.filterConfiguration?.[field.key];
    if (!config || config.type !== FilterType.DATE_RANGE_PICKER) {
      throw new Error('Missing date range filter configuration');
    }
    const dateFrom = filters.value[config.dateFromKey];
    const dateTo = filters.value[config.dateToKey];
    if (
      (dateFrom !== undefined && !isDate(dateFrom)) ||
      (dateTo !== undefined && !isDate(dateTo))
    ) {
      throw new Error('Wrong date filter value: ');
    } else if (!dateFrom && !dateTo) {
      return null;
    } else if (dateFrom && dateTo) {
      return [dateFrom, dateTo];
    }
  }
  function setDateRange(
    field: TableField<T>,
    value: DatePickerProps['modelValue'],
  ) {
    if (!Array.isArray(value) || !value[0] || !value[1]) {
      return;
    }
    const config = props.filterConfiguration?.[field.key];
    if (!config || config.type !== FilterType.DATE_RANGE_PICKER) {
      throw new Error('Missing date range filter configuration');
    }
    (filters.value[config.dateFromKey] as Date) = toStartOfDay(value[0]);
    (filters.value[config.dateToKey] as Date) = toEndOfDay(value[1]);
  }
  function toStartOfDay(date: Date): Date {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    return d;
  }
  function toEndOfDay(date: Date): Date {
    const d = new Date(date);
    d.setHours(23, 59, 59, 999);
    return d;
  }

  const delay = debounce((cb: Function) => {
    cb();
  }, 500);

  function getFilterType(field: TableField<T>): FilterType | undefined {
    return props.filterConfiguration?.[field.key]?.type;
  }
  function getInputPlaceholder(field: keyof T): string | undefined {
    const config = props.filterConfiguration?.[field];
    if (!config) return;
    return config.customLabel
      ? config.customLabel
      : config.simpleLabel
        ? t(`${config.translationKey}.${field.toString()}`)
        : t('filterableTable.filterBy', {
            field: t(`${config.translationKey}.${field.toString()}`),
          });
  }

  const expandedCells = ref<Set<string>>(new Set());
  function getCellId(fieldKey: string, index: number): string {
    return index + fieldKey;
  }
  function toggleCellExpansion(fieldKey: string, index: number) {
    const cellId = getCellId(fieldKey, index);
    if (expandedCells.value.has(cellId)) {
      expandedCells.value.delete(cellId);
    } else {
      expandedCells.value.add(cellId);
    }
    /** To ensure reactivity */
    expandedCells.value = new Set(expandedCells.value);
  }
  function getValue(
    field: TableField<T>,
    index: number,
    item?: T,
  ): string | T[keyof T] {
    const cellId = getCellId(field.key.toString(), index);
    const value = (() => {
      if (!item) return '';
      else if (field.valueGetter) {
        return field.valueGetter(item);
      } else if (field.valueTranslationKey) {
        return t(`${field.valueTranslationKey}.${item[field.key]}`);
      } else return item[field.key];
    })();
    if (field.truncation && !expandedCells.value.has(cellId)) {
      return truncate(value as string, {
        length: field.truncation.maxLength,
      });
    } else return value;
  }

  function handleSortChange(e: DataTableSortEvent) {
    if (typeof e.sortField !== 'string') return;
    pageOptions.value.sortBy = e.sortField as keyof T;
    pageOptions.value.sortOrder = e.sortOrder === 1 ? Order.ASC : Order.DESC;
  }

  function handlePageChange(e: DataTablePageEvent) {
    /** 0-indexed page */
    pageOptions.value.page = e.page + 1;
    pageOptions.value.perPage = e.rows;
  }

  const sortedFilters = computed<SortedFilters<T, U>>(() => {
    const config: FilterConfigurations<T, U> = props.filterConfiguration ?? {};
    const [inputFilters, selectFilters] = [
      FilterType.INPUT,
      FilterType.SELECT,
    ].map((filterType) =>
      pickBy(config, (cfg: FilterConfiguration<U>) => cfg.type === filterType),
    ) as [FilterConfigurations<T, U>, FilterConfigurations<T, U>];
    return { inputFilters, selectFilters };
  });

  const selectValues = computed<SelectOptions<T>>(() => {
    const selectFilterColumns = Object.keys(
      sortedFilters.value.selectFilters,
    ) as (keyof T)[];
    if (mode === TableMode.LAZY) {
      return getSelectValuesFromConfig(selectFilterColumns);
    } else if (mode === TableMode.CLIENT_SIDE) {
      return getSelectValuesFromItems(selectFilterColumns);
    } else throw new Error('Invalid table mode');
  });
  function getSelectValuesFromConfig(
    selectFilterColumns: (keyof T)[],
  ): SelectOptions<T> {
    return selectFilterColumns.reduce((acc, field) => {
      const config = props.filterConfiguration?.[field];
      if (config?.type !== FilterType.SELECT) return acc;
      acc[field] = [SELECT_ALL, ...config.selectOptions];
      return acc;
    }, {} as SelectOptions<T>);
  }
  function getSelectValuesFromItems(
    selectFilterColumns: (keyof T)[],
  ): SelectOptions<T> {
    return selectFilterColumns.reduce((acc, field) => {
      const values = new Set<string>([SELECT_ALL]);
      props.items.forEach((item) => {
        const value = item[field] as string;
        values.add(value);
      });
      acc[field] = Array.from(values);
      return acc;
    }, {} as SelectOptions<T>);
  }

  function getSelectOptionLabel(value: string, field: keyof T): string {
    const config = props.filterConfiguration?.[field];
    if (value === SELECT_ALL) {
      return t('filterableTable.all');
    } else if (config?.translationKey) {
      return t(`${config.translationKey}.${value}`);
    }
    return value;
  }

  function getActionIcon(): IconDefinition {
    switch (props.actionIcon) {
      case 'delete':
        return faTrash;
      case 'ban':
        return faBan;
      default:
        throw new Error('Invalid action icon');
    }
  }

  function getCellSlot(field: TableField<T>): string {
    return `cell(${field.key.toString()})`;
  }

  function openThumbnailPreview(thumbnail: string) {
    selectedThumbnail.value = thumbnail;
    showPreviewDialog.value = true;
  }
</script>

<style lang="scss" scoped>
  .filter-reset-panel {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    margin-bottom: 1rem;
  }

  .selection-hint {
    color: $placeholder-font-color;
  }

  .clickable {
    cursor: pointer;
  }
  @include disabled-icon;
  .checkbox-filter {
    display: flex;
    column-gap: 1rem;
    align-items: center;
  }

  :deep(.selected-row) {
    background-color: $table-row-highlight-color;
    &:hover {
      background-color: $table-row-highlight-color;
    }
  }
  :deep(.selected-row-secondary) {
    background-color: $table-row-secondary-highlight-color;
    &:hover {
      background-color: $table-row-secondary-highlight-color;
    }
  }
  :deep(.danger-row) {
    background-color: $table-row-danger-highlight-color;
    &:hover {
      background-color: $table-row-danger-highlight-color;
    }
  }
</style>
